import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const PHASES = [
  { num: 1, label: 'CT Log Search' },
  { num: 2, label: 'RDAP Check' },
  { num: 3, label: 'DNS TLD Sweep' },
  { num: 4, label: 'DNS Permutations' },
  { num: 5, label: 'Keyword Combos' },
  { num: 6, label: 'Classification' },
];

/* Metric chip shown inline below the progress bar */
function Metric({ label, value, color = 'rgba(251,146,60,0.7)' }) {
  if (value == null || value === 0) return null;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      bgcolor: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.12)',
      borderRadius: 1, px: 0.8, py: 0.2, mr: 0.6, mb: 0.4,
    }}>
      <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.4)', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
    </Box>
  );
}

/* Which metrics to show for each phase */
function PhaseMetrics({ phase, metrics }) {
  if (!metrics || Object.keys(metrics).length === 0) return null;

  const m = metrics;
  let chips = [];

  if (phase === 1) {
    chips = [
      { label: 'Certs', value: m.ct_certs },
      { label: 'Domains', value: m.ct_domains },
      { label: 'Matches', value: m.ct_matches, color: m.ct_matches > 0 ? '#fb923c' : undefined },
    ];
  } else if (phase === 2) {
    chips = [
      { label: 'Exact TLD', value: m.rdap_exact, color: m.rdap_exact > 0 ? '#fb923c' : undefined },
      { label: 'Permutations', value: m.rdap_perms, color: m.rdap_perms > 0 ? '#fb923c' : undefined },
    ];
  } else if (phase === 3) {
    chips = [
      { label: 'Found', value: m.dns_tld_sweep, color: m.dns_tld_sweep > 0 ? '#fb923c' : undefined },
    ];
  } else if (phase === 4) {
    chips = [
      { label: 'Permutations', value: m.dns_perms },
      { label: 'Candidates', value: m.dns_candidates },
      { label: 'Live', value: m.dns_live, color: m.dns_live > 0 ? '#fb923c' : undefined },
    ];
  } else if (phase === 5) {
    chips = [
      { label: 'Combos', value: m.kw_combos },
      { label: 'Variants', value: m.kw_misspellings },
      { label: 'Live', value: m.kw_live, color: m.kw_live > 0 ? '#fb923c' : undefined },
    ];
  } else if (phase === 6) {
    chips = [
      { label: 'Cache hits', value: m.cls_cache_hits },
      { label: 'HTTP checks', value: m.cls_http },
      { label: 'Classified', value: m.cls_done },
      { label: 'Threat Intel', value: m.ti_checking },
      { label: 'Flagged', value: m.ti_flagged, color: m.ti_flagged > 0 ? '#f87171' : undefined },
    ];
  }

  const filtered = chips.filter(c => c.value != null && c.value !== 0);
  if (filtered.length === 0) return null;

  return (
    <Box sx={{ ml: '20px', mt: 0.4, display: 'flex', flexWrap: 'wrap' }}>
      {filtered.map(c => <Metric key={c.label} {...c} />)}
    </Box>
  );
}

function ScanProgress({ progress, startedAt, avgScanSeconds }) {
  const { phase, phase_done, phase_total, domain_index, domain_total, domain_name, message, metrics } = progress || {};

  // Time remaining estimate from the rolling average of past scan durations.
  // Re-computed on every poll re-render (2.5s), so no ticker is needed.
  let etaText = null;
  if (startedAt && avgScanSeconds) {
    const elapsed = (Date.now() - new Date(startedAt + 'Z').getTime()) / 1000;
    const remaining = avgScanSeconds - elapsed;
    if (remaining > 45) {
      const m = Math.round(remaining / 60);
      etaText = m >= 60 ? `~${Math.floor(m / 60)}h ${m % 60}m left` : `~${m}m left`;
    } else {
      etaText = 'wrapping up';
    }
  }

  return (
    <Box sx={{
      mx: { xs: 1.5, sm: 3 }, mt: 2.5,
      p: { xs: 1.5, sm: 2 }, borderRadius: 2,
      bgcolor: 'rgba(251,146,60,0.05)',
      border: '1px solid rgba(251,146,60,0.15)',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(251,146,60,0.85)',
                            textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Scanning
          </Typography>
          {domain_name && (
            <Typography sx={{
              fontSize: '0.85rem', fontWeight: 700, color: '#fb923c',
              letterSpacing: 0.3,
              '@keyframes scanPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
              animation: 'scanPulse 2.5s ease-in-out infinite',
            }}>
              {domain_name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Running totals */}
          {metrics?.total_findings > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.3)' }}>found</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#fb923c', fontVariantNumeric: 'tabular-nums' }}>
                {metrics.total_findings}
              </Typography>
              {metrics?.new_detections > 0 && (
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#f87171' }}>
                  +{metrics.new_detections} new
                </Typography>
              )}
            </Box>
          )}
          {etaText && (
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(251,146,60,0.55)', fontWeight: 600,
                              fontVariantNumeric: 'tabular-nums' }}>
              {etaText}
            </Typography>
          )}
          {domain_total > 1 && (
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(251,146,60,0.5)', fontWeight: 600 }}>
              {domain_index} / {domain_total}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Phase rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {PHASES.map(({ num, label }) => {
          const isActive = phase === num;
          const isDone = phase > num;
          const pct = isActive && phase_total > 0
            ? Math.min(100, Math.round((phase_done / phase_total) * 100))
            : 0;
          const indeterminate = isActive && phase_total === 0;
          const showMessage = isActive && message;

          return (
            <Box key={num} sx={{ opacity: isDone || isActive ? 1 : 0.3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {isDone ? (
                    <CheckIcon sx={{ fontSize: 12, color: '#4ade80' }} />
                  ) : (
                    <Box sx={{
                      width: 12, height: 12, borderRadius: '50%',
                      border: '1.5px solid',
                      borderColor: isActive ? 'rgba(251,146,60,0.7)' : 'rgba(255,255,255,0.15)',
                      bgcolor: isActive ? 'rgba(251,146,60,0.15)' : 'transparent',
                      flexShrink: 0,
                    }} />
                  )}
                  <Typography sx={{
                    fontSize: '0.73rem',
                    color: isDone ? '#4ade80' : isActive ? 'rgba(251,146,60,0.9)' : 'rgba(232,228,248,0.45)',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    {label}
                  </Typography>
                  {showMessage && (
                    <Typography sx={{
                      fontSize: '0.62rem', color: 'rgba(251,146,60,0.45)', fontStyle: 'italic', ml: 0.5,
                      '@keyframes msgPulse': {
                        '0%, 100%': { opacity: 0.7 },
                        '50%': { opacity: 0.3 },
                      },
                      animation: 'msgPulse 2s ease-in-out infinite',
                    }}>
                      {message}
                    </Typography>
                  )}
                </Box>
                {isActive && !indeterminate && (
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(251,146,60,0.6)', fontVariantNumeric: 'tabular-nums' }}>
                    {pct}%
                  </Typography>
                )}
              </Box>

              {isActive && (
                <LinearProgress
                  variant={indeterminate ? 'indeterminate' : 'determinate'}
                  value={pct}
                  sx={{
                    ml: '20px',
                    height: 3, borderRadius: 2,
                    bgcolor: 'rgba(251,146,60,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'rgba(251,146,60,0.7)', borderRadius: 2,
                      transition: 'transform 0.9s linear',
                    },
                    '& .MuiLinearProgress-bar1Indeterminate': { bgcolor: 'rgba(251,146,60,0.7)' },
                    '& .MuiLinearProgress-bar2Indeterminate': { bgcolor: 'rgba(251,146,60,0.4)' },
                  }}
                />
              )}

              {/* Live metrics for active or completed phases */}
              {(isActive || isDone) && <PhaseMetrics phase={num} metrics={metrics} />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ScanProgress;
